import { createContext, useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import axios from "axios";

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const token = Cookies.get("userToken");

  // Fetch pending connection requests
  const fetchReceivedRequests = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/requestPending`, 
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setReceivedRequests(res.data.data || []);
      setUnreadCount(res.data.data?.length || 0);
    } catch (error) {
      console.error("Error fetching received requests:", error);
    }
  }, [token]);

  // Fetch sent connection requests
  const fetchSentRequests = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/getSentRequests`, 
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSentRequests(res.data.data || []);
    } catch (error) {
      // API may not exist yet, so handle gracefully
      console.warn("Sent requests endpoint not available:", error.message);
      setSentRequests([]);
    }
  }, [token]);

  // Initial fetch on mount
  useEffect(() => {
    if (token) {
      fetchReceivedRequests();
      fetchSentRequests();
    }
  }, [token, fetchReceivedRequests, fetchSentRequests]);

  // Poll for new requests every 30 seconds
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      fetchReceivedRequests();
    }, 30000);
    return () => clearInterval(interval);
  }, [token, fetchReceivedRequests]);

  // Add notification
  const addNotification = (message, type = "info") => {
    const id = Date.now();
    const notification = { id, message, type, timestamp: new Date() };
    setNotifications((prev) => [...prev, notification]);
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        receivedRequests,
        sentRequests,
        unreadCount,
        addNotification,
        removeNotification,
        fetchReceivedRequests,
        fetchSentRequests,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
